import React, { useState } from 'react';
import { X, Star, Loader2 } from 'lucide-react';
import { useSubmitBookingReview } from '../../services/bookingService';
import { useToastContext } from '../../contexts/ToastContext';
import ModalShell from '../shared/ModalShell';

const ReviewModal = ({ isOpen, onClose, bookingId, onSuccess }) => {
 const [rating, setRating] = useState(0);
 const [hoveredRating, setHoveredRating] = useState(0);
 const [title, setTitle] = useState('');
 const [comment, setComment] = useState('');
 const [errors, setErrors] = useState({});
 
 const { success, error: showError } = useToastContext();
 const submitReviewMutation = useSubmitBookingReview({
 onSuccess: (data) => {
 success('Review submitted successfully! It will be visible after admin approval.');
 onSuccess?.();
 handleClose();
 },
 onError: (error) => {
 showError(error?.response?.data?.message || 'Failed to submit review');
 }
 });

 const handleClose = () => {
 setRating(0);
 setHoveredRating(0);
 setTitle('');
 setComment('');
 setErrors({});
 onClose();
 };

 const validate = () => {
 const newErrors = {};
 if (rating === 0) {
 newErrors.rating = 'Please select a rating';
 }
 if (!title.trim()) {
 newErrors.title = 'Please enter a review title';
 }
 if (!comment.trim()) {
 newErrors.comment = 'Please enter a review comment';
 }
 setErrors(newErrors);
 return Object.keys(newErrors).length === 0;
 };

 const handleSubmit = (e) => {
 e.preventDefault();
 if (!validate()) return;

 submitReviewMutation.mutate({
 bookingId,
 rating,
 title: title.trim(),
 comment: comment.trim()
 });
 };

 return (
 <ModalShell
 isOpen={isOpen}
 onClose={() => !submitReviewMutation.isPending && handleClose()}
 labelledBy="review-modal-title"
 panelClassName="max-w-md"
 >
 <div className="bg-surface rounded-lg border border-border shadow-sm p-6 relative overflow-hidden">
 <button
 type="button"
 onClick={handleClose}
 className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 transition-colors p-1 rounded-md hover:bg-surface-muted"
 disabled={submitReviewMutation.isPending}
 aria-label="Close"
 >
 <X className="w-5 h-5" />
 </button>

 <div className="mb-6 pr-8">
 <h2
 id="review-modal-title"
 className="text-xl font-bold text-ink mb-2"
 >
 Write a Review
 </h2>
 <p className="text-sm text-ink-secondary">
 Share your experience with this vendor
 </p>
 </div>

 <form onSubmit={handleSubmit} className="space-y-6">
 <div>
 <label className="block text-sm font-medium text-ink-secondary mb-2">
 Rating <span className="text-red-500">*</span>
 </label>
 <div className="flex items-center gap-2">
 {[1, 2, 3, 4, 5].map((star) => (
 <button
 key={star}
 type="button"
 onClick={() => setRating(star)}
 onMouseEnter={() => setHoveredRating(star)}
 onMouseLeave={() => setHoveredRating(0)}
 className="focus:outline-none transition-transform hover:scale-110"
 disabled={submitReviewMutation.isPending}
 >
 <Star
 className={`w-8 h-8 transition-colors ${ star <= (hoveredRating || rating) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300' }`}
 />
 </button>
 ))}
 {rating > 0 && (
 <span className="ml-2 text-sm text-ink-secondary">
 {rating} {rating === 1 ? 'star' : 'stars'}
 </span>
 )}
 </div>
 {errors.rating && (
 <p className="mt-1 text-sm text-red-600">{errors.rating}</p>
 )}
 </div>

 <div>
 <label htmlFor="review-title" className="block text-sm font-medium text-ink-secondary mb-2">
 Review Title <span className="text-red-500">*</span>
 </label>
 <input
 id="review-title"
 type="text"
 value={title}
 onChange={(e) => setTitle(e.target.value)}
 placeholder="e.g., Great service!"
 maxLength={255}
 className="w-full px-3 py-2 border border-border bg-surface text-ink rounded-xl focus:ring-2 focus:ring-ink/40 focus:border-ink"
 disabled={submitReviewMutation.isPending}
 />
 {errors.title && (
 <p className="mt-1 text-sm text-red-600">{errors.title}</p>
 )}
 </div>

 <div>
 <label htmlFor="review-comment" className="block text-sm font-medium text-ink-secondary mb-2">
 Your Review <span className="text-red-500">*</span>
 </label>
 <textarea
 id="review-comment"
 value={comment}
 onChange={(e) => setComment(e.target.value)}
 placeholder="Tell others about your experience..."
 rows={5}
 maxLength={1000}
 className="w-full px-3 py-2 border border-border bg-surface text-ink rounded-xl focus:ring-2 focus:ring-ink/40 focus:border-ink resize-none"
 disabled={submitReviewMutation.isPending}
 />
 <p className="mt-1 text-xs text-ink-tertiary">
 {comment.length}/1000 characters
 </p>
 {errors.comment && (
 <p className="mt-1 text-sm text-red-600">{errors.comment}</p>
 )}
 </div>

 <div className="flex gap-3 pt-4">
 <button
 type="button"
 onClick={handleClose}
 disabled={submitReviewMutation.isPending}
 className="flex-1 px-4 py-2 border border-border text-ink rounded-md hover:bg-surface-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
 >
 Cancel
 </button>
 <button
 type="submit"
 disabled={submitReviewMutation.isPending}
 className="flex-1 px-4 py-2 bg-gray-900 text-white rounded-md hover:bg-black transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
 >
 {submitReviewMutation.isPending ? (
 <>
 <Loader2 className="w-4 h-4 animate-spin" />
 Submitting...
 </>
 ) : (
 'Submit Review'
 )}
 </button>
 </div>
 </form>
 </div>
 </ModalShell>
 );
};

export default ReviewModal;
